import { Component, effect, inject, Input, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  Workspace,
  WorkspaceType,
  WorkspaceTypeKey,
} from '../../../models/workspace';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { CheckboxModule } from 'primeng/checkbox';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { NgClass, NgIf } from '@angular/common';
import { PaginatorModule } from 'primeng/paginator';
import { WorkspaceStore } from '../../../stores/workspace/workspace.state';
import { ActivatedRoute, Router } from '@angular/router';
import { AppUtilityService } from '../../../utility/app.utility';
import { RoutingService } from '../../../services/routing.service';
import { BaseFormComponent } from '../../molecules/base-form/base-form.component';
import { PermissionService } from '../../../services/permission.service';
import { FormFieldWrapperComponent } from '../../molecules/form-field-wrapper/form-field-wrapper.component';
import { DividerModule } from 'primeng/divider';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { FormFooterComponent } from '../../molecules/form-footer/form-footer.component';
import { getState, patchState } from '@ngrx/signals';
import { workspaceTypes } from '../../../stores/workspace/workspace.selectors';
import { InputSwitchModule } from 'primeng/inputswitch';
import { MenuItem } from 'primeng/api';
import { BadgeModule } from 'primeng/badge';
import { MenuModule } from 'primeng/menu';
import { TabMenuModule } from 'primeng/tabmenu';
import { TableModule } from 'primeng/table';
import { CategoryPickListComponent } from '../../molecules/category-pick-list/category-pick-list.component';
import { HeaderComponent } from '../../atoms/header/header.component';
import { TopicPickListComponent } from '../../molecules/topic-pick-list/topic-pick-list.component';
import { BookmarkCategoryPickListComponent } from '../../molecules/bookmark-category-pick-list/bookmark-category-pick-list.component';
import { BulkOperationService } from '../../../services/bulk-operation.service';
import { DataLayerTypePickListComponent } from '../../molecules/data-layer-type-pick-list/data-layer-type-pick-list.component';
import { makeUniqueStringArray } from '../../../utility/array.utility';
import { ChipsModule } from 'primeng/chips';
import { DebugComponent } from '../../atoms/debug/debug.component';
import { yearArrayValidator } from '../../../validators/yearArray.validator';
import { AlertComponent } from '../../atoms/alert/alert.component';
import { TopicStore } from '../../../stores/topic/topic.state';
import { StyleClassModule } from 'primeng/styleclass';

@Component({
  selector: 'app-workspace-form',
  standalone: true,
  imports: [
    AutoCompleteModule,
    ButtonModule,
    CardModule,
    CheckboxModule,
    InputTextModule,
    InputTextareaModule,
    NgIf,
    PaginatorModule,
    ReactiveFormsModule,
    FormFieldWrapperComponent,
    DividerModule,
    FormFooterComponent,
    InputSwitchModule,
    BadgeModule,
    MenuModule,
    TabMenuModule,
    TableModule,
    CategoryPickListComponent,
    HeaderComponent,
    TopicPickListComponent,
    BookmarkCategoryPickListComponent,
    DataLayerTypePickListComponent,
    ChipsModule,
    DebugComponent,
    AlertComponent,
    StyleClassModule,
  ],
  templateUrl: './workspace-form.component.html',
  styleUrl: './workspace-form.component.scss',
})
export class WorkspaceFormComponent
  extends BaseFormComponent<Workspace>
  implements OnInit
{
  readonly store = inject(WorkspaceStore);
  override form: FormGroup = new FormGroup({});
  override itemToEdit?: Workspace;
  @Input() type?: string;

  override formInitialized = false;

  types: WorkspaceType[] = workspaceTypes;
  active = 'indicatorCategory';
  workspaceChildren: any;
  menuItems: MenuItem[] = [];
  activeTab?: MenuItem;
  groupedKeys: Record<string, string[]> | undefined;

  constructor(
    router: Router,
    route: ActivatedRoute,
    appUtilityService: AppUtilityService,
    routingService: RoutingService,
    permissionService: PermissionService,
    confirmationDialogService: ConfirmationDialogService,
    private bulkOperationService: BulkOperationService,
    private fb: FormBuilder
  ) {
    super(
      router,
      route,
      appUtilityService,
      routingService,
      permissionService,
      confirmationDialogService
    );

    effect(() => {
      const state = getState(this.store);
      if (state.selectedId) {
        this.initializeForm();
        this.workspaceChildren = this.permissionService.getWorkspaceChildren(
          state.selectedId
        );
        this.setMenuItems();
      }
    });
  }

  /**
   * Sets the `menuItems` property with a predefined list of menu objects, each containing a label, key, icon,
   * and a command function that updates the `active` property. Also, initializes the `activeTab` property
   * with the first menu item if it is not already set.
   *
   * @return {void} This method does not return any value.
   */
  setMenuItems(): void {
    this.menuItems = [
      {
        label: 'Ordner',
        key: 'indicatorCategory',
        icon: 'fas fa-folder',
        command: event => {
          this.active = 'indicatorCategory';
        },
      },
      {
        label: 'Räume',
        key: 'dataLayerType',
        icon: 'fa-regular fa-map',
        command: event => {
          this.active = 'dataLayerType';
        },
      },
      {
        label: 'Standorte',
        key: 'poiDataLayerType',
        icon: 'fas fa-map-location-dot',
        command: event => {
          this.active = 'poiDataLayerType';
        },
      },
      {
        label: 'Themenbereiche',
        key: 'bookmarkCategory',
        icon: 'fas fa-layer-group',
        command: event => {
          this.active = 'bookmarkCategory';
        },
      },
      {
        label: 'Themen',
        key: 'topic',
        icon: 'fas fa-book-open',
        command: event => {
          this.active = 'topic';
        },
      },
    ];
    if (!this.activeTab) this.activeTab = this.menuItems[0];
  }

  /**
   * Lifecycle hook that is called after data-bound properties of a directive are initialized.
   * This method enhances the initialization by ensuring a specific form setup is executed if not already initialized.
   *
   * @return {void} Does not return a value.
   */
  override ngOnInit(): void {
    super.ngOnInit();
    if (!this.formInitialized) this.initializeForm();
  }

  /**
   * Initializes the form by setting up form controls and their default values based on the selected item.
   * Also, sets the form as initialized once configuration is complete.
   *
   * @return {void} Does not return any value.
   */
  initializeForm(): void {
    this.itemToEdit = this.store.selectedItem();
    this.form = this.fb.group({
      name: [this.itemToEdit?.name || '', Validators.required],
      shy: [this.itemToEdit?.shy || false],
      allowedYears: [this.itemToEdit?.allowedYears || [], yearArrayValidator()],
    });
    this.formInitialized = true;
  }

  /**
   * Handles the submission process by invoking the submit action.
   * This method is typically triggered by a user event, such as a form submission.
   *
   * @return {void} This method does not return a value.
   */
  onSubmit(): void {
    if (this.form.valid) {
      const ws: any = this.form.value;
      ws.typeGroups = this.groupedKeys;

      patchState(this.store, {
        triggerOptions: { workspaceChildren: this.workspaceChildren },
      });
      this.submit(ws);
    }
  }

  /**
   * Adds a new set of children to the workspace under the specified key.
   *
   * @param {WorkspaceTypeKey} key - The key identifying the workspace type where children will be added.
   * @param {string[]} ids - The array of IDs representing the children to add to the specified workspace key.
   * @return {void} This method does not return a value.
   */
  addToWorkspaceChildren(key: WorkspaceTypeKey, ids: string[]): void {
    this.workspaceChildren[key] = ids;
  }

  /**
   * Handles changes to type or group, parses the provided IDs and keys, and updates workspace children accordingly.
   *
   * @param {string[]} ids - An array of strings representing IDs, which may include keys in the format `_id#key`.
   * @param {string} type - A string representing the type of the workspace children to update. Can be either `Polygon` or `Point`.
   * @return {void} This method does not return any value.
   */
  typeOrGroupChanged(ids: string[], type: 'Polygon' | 'Point'): void {
    // Parse ids-param format: _id#key
    const parsedIds: string[] = [];
    const keysByIds: Record<string, string[]> | undefined = {};

    ids.forEach(idParam => {
      if (idParam.includes('#')) {
        const [_id, keyValue] = idParam.split('#');
        parsedIds.push(_id);

        // Initialize array if it doesn't exist
        if (!keysByIds[_id]) {
          keysByIds[_id] = [];
        }

        // Add key to the array if it's not already present
        if (!keysByIds[_id].includes(keyValue)) {
          keysByIds[_id].push(keyValue);
        }
      } else {
        // Handle ids without keys (just the _id part)
        parsedIds.push(idParam);
      }
    });

    this.groupedKeys =
      Object.keys(keysByIds).length > 0 ? keysByIds : undefined;

    // Update workspace children with the parsed data
    this.addToWorkspaceChildren(
      type === 'Polygon' ? 'dataLayerType' : 'poiDataLayerType',
      makeUniqueStringArray(parsedIds)
    );
  }
}
